import { getUserDataByUserId } from "@/api/user";
import { getCurrentAuthUser } from "@/api/auth";
import IconButton from "@/components/ui/Buttons/IconButton";
import SolidButton from "@/components/ui/Buttons/SolidButton";
import Div from "@/components/ui/div";
import DropdownMenu from "@/components/ui/DropdownMenu";
import PostAvatar from "@/components/ui/PostAvatar";
import FeedFilterDropdown from "./FeedFilterDropdown";

import { useAuthContext } from "@/context/auth/useAuthContext";
import { enqueueSnackbar } from "notistack";
import { Fragment, useEffect, useState } from "react";
import Cookies from "js-cookie";
import HotpleLogo from "@/components/hotple-logo";
import useEffectivePathname from "@/hooks/useEffectivePathname";
import { useTranslations } from "@/lib/static-intl";
import {
  ROUTES,
  buildCurrentUserProfilePath,
  isCurrentFeedPath,
  isCurrentPlayPath,
} from "@/constants/routes";
import { useGlobalContext } from "@/context/global/GlobalContext";

const UL_Sidebar = ({
  routeTo,
  handleFeedbackModalOpen,
  handlePlaceRegisterModalOpen,
}) => {
  const t = useTranslations("sidebar");
  const apiT = useTranslations("api");
  const pathname = useEffectivePathname();
  const { user, isLoading, setIsLoginModalOpen } = useAuthContext();
  const [userDetails, setUserDetails] = useState({});
  const { setFilter, setShowDropdown } = useGlobalContext();
  const [isHydrated, setIsHydrated] = useState(false);

  // Handle hydration to prevent SSR/client mismatch
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const isFeedPage = isCurrentFeedPath(pathname);
  const isPlayPage = isCurrentPlayPath(pathname);
  const currentUserProfilePath = buildCurrentUserProfilePath(
    user?.username,
    user?.userId
  );
  const userRole = String(user?.role || "").toLowerCase();
  const userDetailsRole = String(userDetails?.role || "").toLowerCase();
  const cookieRole = isHydrated
    ? String(Cookies.get("role") || "").toLowerCase()
    : "";
  const isAdminUser =
    isHydrated &&
    (userRole === "admin" ||
      userDetailsRole === "admin" ||
      cookieRole === "admin");


  const tabs = [
    {
      id: "2",
      title: "release",
      icon: "mynaui:plus-square",
      // 새 탭으로 열지 않는다 — 누를 때마다 탭이 쌓이고 뒤로가기가 안 됐다
      // (2026-08-11 검수). 관리자는 별도 작업 공간이라 새 탭을 그대로 둔다.
      link: ROUTES.creator.upload,
    },
    {
      id: "3",
      title: "places",
      icon: "custom_place_icon",
      link: ROUTES.current.place,
    },
    {
      id: "admin",
      title: "admin",
      icon: "clarity:administrator-line",
      link: ROUTES.admin.dataCenter,
      newTab: true,
    },
    {
      id: "4",
      title: "notifications",
      icon: "ci:bell",
      link: ROUTES.user.notifications,
      notifTooltip: true,
    },
    {
      id: "5",
      title: "messages",
      icon: "ri:chat-ai-line",
      link: ROUTES.user.messages,
      msgTooltip: true,
    },
    // {
    //   id: "6",
    //   title: "my plan",
    //   icon: "mdi:invoice-schedule",
    //   link: "/plans",
    // },
  ];

  // const loginData = [
  //   {
  //     id: "1",
  //     title: "find_high_quality_content_to_understand_you_better",
  //     icon: "iconamoon:like-light",
  //   },
  //   {
  //     id: "2",
  //     title: "search_latest_information_on_planting_and_weeding",
  //     icon: "ph:plant-light",
  //   },
  //   {
  //     id: "3",
  //     title: "view_favorited_and_liked_notes",
  //     icon: "hugeicons:star",
  //   },
  //   {
  //     id: "4",
  //     title: "communicate_and_connect_better_with_others",
  //     icon: "basil:comment-outline",
  //   },
  // ];

  const handleLogin = () => {
    setIsLoginModalOpen((prev) => !prev);
  };

  const routeCurrentUserProfile = async () => {
    if (!user?.userId) {
      setIsLoginModalOpen(true);
      return;
    }

    let target = buildCurrentUserProfilePath(null, user.userId);

    try {
      const currentAuth = await getCurrentAuthUser();
      if (currentAuth?.token) {
        localStorage.setItem("jwtToken", currentAuth.token);
      }

      const latestUser = currentAuth?.user;
      target =
        buildCurrentUserProfilePath(
          latestUser?.username,
          latestUser?.userId || latestUser?.id || user.userId
        ) || target;
    } catch {
      try {
        const latestUser = await getUserDataByUserId(user.userId, { page: 1 });
        target =
          buildCurrentUserProfilePath(
            latestUser?.username,
            latestUser?.userId || latestUser?.id || user.userId
          ) || target;
      } catch {
        target = buildCurrentUserProfilePath(null, user.userId);
      }
    }

    routeTo(target);
  };

  const handleActiveTab = (tab) => {};

  const fetchCurrentUserDetails = async () => {
    try {
      const userDetails = await getUserDataByUserId(user?.userId, { page: 1 });
      if (userDetails) setUserDetails(userDetails);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        {
          variant: "error",
        }
      );
    }
  };

  useEffect(() => {
    if (user && user?.userId) {
      fetchCurrentUserDetails();
    }
  }, [user]);

  const visibleTabs = tabs;

  return (
    <Div
      className="hidden lg:flex flex-col justify-between pb-4 h-full dark:bg-custom_black bg-dark_primary_label w-[300px] flex-none xl:!px-4"
    >
      <div className="flex flex-col gap-2 w-full">
        <div className="flex items-center mt-2 mb-2 ml-4">
          <div
            className="cursor-pointer"
            onClick={() => {
              if (isFeedPage) {
                !user && routeTo(ROUTES.current.play);
                setShowDropdown((prev) => !prev);
              } else {
                routeTo(ROUTES.current.play);
                setFilter("all");
              }
            }}
          >
            <HotpleLogo />
          </div>
          {user && isFeedPage && (
            <FeedFilterDropdown
              onChange={(option) => {
                // updateQueryParam("filter", option === "all" ? "" : option)
                setFilter(option);
              }}
            />
          )}
        </div>

        {/* Tabs div  */}
        <div className="flex flex-col gap-2 w-full">
          <IconButton
            icon={"custom_play_icon"}
            iconClass={"!w-5 !h-5"}
            className={`w-full ${
              isCurrentPlayPath(pathname)
                ? "dark:bg-dark_bg_grey bg-light_bg_grey"
                : ""
            }`}
            notifTooltip={false}
            onClick={() => {
              routeTo(ROUTES.current.play);
              handleActiveTab(ROUTES.current.play);
            }}
          >
            {t("play")}
          </IconButton>
          {visibleTabs?.map((tab, index) => {
            if (
              !isAdminUser &&
              tab.title == "admin"
            ) {
              return <Fragment key={index}></Fragment>;
            }
            return (
              <Fragment key={index}>
                {tab?.newTab ? (
                  <IconButton
                    icon={tab?.icon}
                    iconClass={"!w-5 !h-5"}
                    className={`w-full ${
                      pathname.includes(tab.link)
                        ? "dark:bg-dark_bg_grey bg-light_bg_grey"
                        : ""
                    }`}
                    onClick={() => {
                      if (!user) {
                        setIsLoginModalOpen(true);
                        return;
                      }
                      window.open(tab?.link, "_blank");
                      handleActiveTab(tab);
                    }}
                  >
                    {t(tab?.title)}

                  </IconButton>
                ) : (
                  <IconButton
                    icon={tab?.icon}
                    iconClass={"!w-5 !h-5"}
                    className={`w-full ${
                      pathname.includes(tab.link)
                        ? "dark:bg-dark_bg_grey bg-light_bg_grey"
                        : ""
                    }`}
                    notifTooltip={tab?.notifTooltip}
                    msgTooltip={tab?.msgTooltip}
                    onClick={() => {
                      if (!user && !tab?.link?.startsWith(ROUTES.current.place)) {
                        setIsLoginModalOpen(true);
                        return;
                      }
                      routeTo(tab?.link);
                      handleActiveTab(tab);
                    }}
                  >
                    {t(tab?.title)}

                  </IconButton>
                )}
              </Fragment>
            );
          })}

          {isHydrated && user && user?.userId && (
            <button
                className={`flex items-end gap-3 py-3 px-3 md:px-5 mb-4 !leading-4 font-semibold dark:text-white text-light_primary_label dark:hover:bg-dark_bg_grey hover:bg-light_bg_grey outline-none rounded-full w-full ${
                currentUserProfilePath && pathname.startsWith(currentUserProfilePath)
                  ? "dark:bg-dark_bg_grey bg-light_bg_grey"
                  : ""
              }`}
              name="btn"
              onClick={() => {
                routeCurrentUserProfile();
                handleActiveTab("user profile");
              }}
            >
              <PostAvatar src={userDetails?.profilePicture} />
              <span className="!leading-4 mb-[3px]">{t("me")}</span>
            </button>
          )}
        </div>

        {(!isHydrated || isLoading) && (
          <div
            className="h-10 w-full rounded-full bg-light_bg_grey dark:bg-dark_bg_grey"
            aria-hidden="true"
          />
        )}

        {isHydrated && !isLoading && !user && (
          <div className="flex flex-col gap-3">
            {/* Login button */}
            <SolidButton className={"w-full"} onClick={handleLogin}>
              {t("login")}
            </SolidButton>

            {/* <div className="flex flex-col border dark:text-dark_primary_label dark:border-neutral-800 border-neutral-200 rounded-2xl px-3 py-2.5">
              <H6
                className={
                  "mb-1 dark:!text-dark_secondary_label !text-light_secondary_label"
                }
              >
                {t("login_now")}
              </H6>
              {loginData?.map((item, index) => (
                <div key={index} className="flex gap-1 my-0.5">
                  <Iconify
                    icon={item.icon}
                    className={
                      "!min-w-4 !h-4 dark:text-dark_tertiary_label text-neutral-500"
                    }
                  />
                  <Span
                    className={
                      "dark:!text-dark_tertiary_label !text-neutral-500 "
                    }
                  >
                    {t(item.title)}
                  </Span>
                </div>
              ))}
            </div> */}
          </div>
        )}
      </div>
      <div className="mb-0 mt-1">
        <DropdownMenu
          handleFeedbackModalOpen={handleFeedbackModalOpen}
          handlePlaceRegisterModalOpen={handlePlaceRegisterModalOpen}
        >
          <IconButton
            icon={"fluent:navigation-24-filled"}
            className={"w-full"}
            iconClass={"!w-5 !h-5"}
          >
            {t("more")}
          </IconButton>
        </DropdownMenu>
      </div>
    </Div>
  );
};

export default UL_Sidebar;
